import { Component, OnInit, Output, SimpleChanges, Input, EventEmitter, ViewChild, ElementRef, Renderer, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer, SafeStyle, SafeUrl } from '@angular/platform-browser';
import { NgForm, ControlContainer, AbstractControl, ValidatorFn, AsyncValidatorFn } from '@angular/forms';

const imageFrameWidth: number = 120;
const imageFrameHeight: number = 160;
const maxFileSize: number = 5000000;

@Component({
    selector: 'file-uploader',
    templateUrl: './file-uploader.component.html',
    styleUrls: ['./file-uploader.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class FileUploaderComponent implements OnInit {
    private fileUploadError: IFileUploadError = { Type: "", Message: "" };

    @Input('imageUpload') imageUpload: boolean = false;
    @Input('otherFileUpload') otherFileUpload: boolean = false;
    @Input('uploadText') uploadText: string = "";
    @Input('file-uploader-width') fileUploaderWidth: string = "140";
    @Input('id') id: string = "";
    @Input('form') form: NgForm;
    @Output() emitPicture: EventEmitter<IFileObject> = new EventEmitter<IFileObject>();
    @Output() emitOtherFile: EventEmitter<IFileObject> = new EventEmitter<IFileObject>();
    @Output() emitOpenFile: EventEmitter<IFileObject> = new EventEmitter<IFileObject>();
    @Output() emitError: EventEmitter<IFileUploadError> = new EventEmitter<IFileUploadError>();
    @Output("imageRemoved") imageRemoved = new EventEmitter();
    @Output("fileRemoved") fileRemoved: EventEmitter<IFileObject> = new EventEmitter<IFileObject>();

    localImgUrl: string;
    noImage: boolean = true;
    noFile: boolean = true;
    localImageLoaded: boolean = false;
    localFileLoaded: boolean = false;
    fileArray: IFileObject[] = Array();
    isUploaded: boolean = false;

    ngOnInit() {
        // set upload-text
        if (!this.uploadText || this.uploadText.length == 0) {
            if (this.imageUpload)
                this.uploadText = "UPLOAD PHOTO";
            else
                this.uploadText = "UPLOAD FILE";
        }
        // set id
        if (this.id || this.id.length === 0) {
            if (this.imageUpload)
                this.id = "imageUploader";
            else
                this.id = "fileUploader";
        }
    }

    readImageFiles = (files: any, callback: Function, index = 0) => {
        let reader = new FileReader();
        if (index in files) {
            if (this.imageTypeValidator(files[index])) {
                this.readImageFile(files[index], reader, callback);
            }
        }
    }

    readOtherFiles = (files: any, callback: Function, index = 0) => {
        let reader = new FileReader();
        if (index in files) {
            if (this.fileSizeValidator(files[index])) {
                if (this.fileTypeValidator(files[index])) {
                    this.readOtherFile(files[index], reader, callback);
                    this.displayFileName(files[index]);
                }
            }
            else {
                this.fileUploadError.Type = "File size too large.";
                this.fileUploadError.Message = "The file you have selected is too big. Please upload a file below 5Mb.";
                this.emitError.emit(this.fileUploadError);
            }
        }
    }

    displayFileName = (file: any) => {
        var fileSizeInKb = file.size / 1024;
        this.fileArray.push(<IFileObject>{ Name: file.name, Content: null, Type: file.type, Size: fileSizeInKb.toFixed(4) });
    }

    readImageFile = (file: any, reader: any, callback: Function) => {
        reader.onload = () => {
            var result = reader.result;
            var img = document.createElement("img");
            img.src = result;
            img.onload = function () {
                callback(img, file.name, file.type, file.size);
            };
        }
        reader.readAsDataURL(file);
    }

    readOtherFile = (file: any, reader: any, callback: Function) => {
        reader.onload = () => {
            let fileSizeInKb = file.size / 1024;
            let fileObject: IFileObject = { Name: file.name, Type: file.type, Content: reader.result, Size: fileSizeInKb.toFixed(4) };
            callback(fileObject);
        }
        reader.readAsDataURL(file);
    }

    resize = (img: any, MAX_WIDTH: number, MAX_HEIGHT: number): any => {
        var imgCopy = img;
        var width = img.width;
        var height = img.height;
        if (width > height) {
            if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width;
                width = MAX_WIDTH;
            }
        } else {
            if (height > MAX_HEIGHT) {
                width *= MAX_HEIGHT / height;
                height = MAX_HEIGHT;
            }
        }
        var canvas = document.createElement("canvas");
        var copyCanvas = canvas;

        canvas.width = width;
        canvas.height = height;

        var ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        var dataUrl = canvas.toDataURL();

        return dataUrl;
        //below code is to enable multiple files to be uploaded. (add 'multiple' to input in template) make imgSrc an array and push them into it.
        //this.file_srcs.pop();
        //this.file_srcs.push(resized_jpeg);
        //this.readFiles(files, index + 1);
    }

    fileSizeValidator = (file: any) => {
        if (file.size < maxFileSize) {
            return true;
        }
        else {
            return false;
        }
    }

    imageTypeValidator(file: any): boolean {
        let result: boolean = false;
        switch (file.type) {
            case 'image/gif': // .gif
            case 'image/jpeg': // .jpeg, .jpg
            case 'image/png': // .png
            case 'image/svg+xml': // .svg
                result = true;
                break;
            default:
                this.fileUploadError.Type = "Image not found.";
                this.fileUploadError.Message = "The file you have selected is not a vaild image.";
                this.emitError.emit(this.fileUploadError);
        }
        return result;
    }

    fileTypeValidator = (file: any): boolean => {
        let result: boolean = false;
        switch (file.type) {
            case 'application/msword': // .doc
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': // .docx
            case 'image/jpeg': // .jpeg, .jpg
            case 'application/pdf': // .pdf
            case 'image/png': // .png
            case 'text/plain': // .txt
            case 'application/vnd.ms-excel': // .xls
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': // .xlsx
                result = true;
                break;
            default:
                this.fileUploadError.Type = "Unsupported file format.";
                this.fileUploadError.Message = "Please upload your file in one of the following formats: Word, JPEG, PDF, PNG, Text or Excel.";
                this.emitError.emit(this.fileUploadError);
        }
        return result;
    }

    onImageFileChange = (input: any) => {
        this.readImageFiles(input.files, this.onImageFileReadCallBack);

        this.isUploaded = true;
        if (this.form && this.form.control.get(this.id))
            this.form.control.get(this.id).markAsTouched({ onlySelf: true });
    }

    onOtherFileChange = (input: any) => {
        this.readOtherFiles(input.files, this.onOtherFileReadCallBack);
    }

    onImageFileReadCallBack = (img: any, filename: string, filetype: string, fileSize: number) => {
        this.localImgUrl = this.resize(img, imageFrameWidth, imageFrameHeight);
        this.localImageLoaded = true;
        this.noImage = false;
        let fileSizeInKb = fileSize / 1024;
        let file: IFileObject = { Name: filename, Type: filetype, Content: this.localImgUrl, Size: fileSizeInKb.toFixed(4) };
        this.emitPicture.emit(file);

        this.isUploaded = true;
        if (this.form) {
            const control = this.form.control.get(this.id);
            if (control) {
                this.setValue(control, this.isUploaded);
            }
        }
    }

    onOtherFileReadCallBack = (file: any) => {
        this.localFileLoaded = true;
        this.noFile = false;
        this.emitOtherFile.emit(file);

        this.isUploaded = true;
        if (this.form) {
            const control = this.form.control.get(this.id);
            if (control) {
                this.setValue(control, this.isUploaded);
            }
        }
    }

    private setValue(control: AbstractControl, value) {
        control.markAsTouched({ onlySelf: true });
        control.setValue(value);
        control.updateValueAndValidity({ onlySelf: true });
    }

    onFileClicked(file: IFileObject) {
        this.emitOpenFile.emit(file);
    }

    onFakeButtonClicked($event, inputElement) {
        inputElement.click();
    }

    onFileRemove(fileObject: IFileObject) {
        var removedItem = this.fileArray.splice(this.fileArray.indexOf(fileObject), 1);
        (<HTMLInputElement>document.getElementById("otherFileInput")).value = "";
        this.fileRemoved.emit(removedItem[0]);

        this.isUploaded = (this.fileArray.length > 0);
        if (this.form) {
            const control = this.form.control.get(this.id);
            if (control) {
                this.setValue(control, this.isUploaded);
            }
        }
    }

    onImageRemove() {
        this.localImgUrl = null;
        this.noImage = true;
        this.localImageLoaded = false;
        (<HTMLInputElement>document.getElementById("imageInput")).value = "";
        this.imageRemoved.emit();

        this.isUploaded = (this.fileArray.length > 0);
        if (this.form) {
            const control = this.form.control.get(this.id);
            if (control) {
                this.setValue(control, this.isUploaded);
            }
        }
    }

    activateUploader() {
        if (this.form && this.form.control.get(this.id)) {
            this.form.control.get(this.id).markAsTouched({ onlySelf: true });
            this.form.control.get(this.id).updateValueAndValidity({ onlySelf: true });
        }
    }

    setUploadValidator() {
        if (this.form && this.form.control.get(this.id))
            this.form.control.get(this.id).setValidators([UploadValidator(true)]);
    }

    removeUploadValidator() {
        if (this.form && this.form.control.get(this.id))
            this.form.control.get(this.id).clearValidators();
    }

    get valid(): boolean {
        return (this.form && this.form.control.get(this.id) && this.form.control.get(this.id).valid);
    }
}

export interface IFileObject {
    Name: string;
    Type: string;
    Content: string;
    Size: string;
}

export interface IFileUploadError {
    Type: string;
    Message: string;
}

export function UploadValidator(isRequired: boolean): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
        if (control.enabled && control.touched && control.value != isRequired)
            return { 'uploader': true };
        return null;
    };
}



// WEBPACK FOOTER //
// ./src/app/widgets/file-uploader/file-uploader.component.ts